import { useEffect, useState } from "react";
import { IRestaurantDetail } from "../models/IRestaurantDetail";
import { City } from "../models/IOrders";
import { getCityList } from "../services/locations";
import { postRestaurant, putRestaurants } from "../services/restaurant";

const inputStyle: React.CSSProperties = {
  borderRadius: "10px",
  border: "1.5px solid #dee2e6",
  fontSize: "0.92rem",
};

function AddRestaurantPage({ props, isUpdate, onSaved }: { props?: IRestaurantDetail; isUpdate?: boolean; onSaved?: () => void }) {
  const [updateRestaurantData, setupdateRestaurantData] = useState<IRestaurantDetail | null>(null);
  const [formAction, setFormAction] = useState<"add" | "update">("add");
  const [cityList, setCityList] = useState<City[]>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    getCityList().then((res) => { if (res.data) setCityList(res.data); });
  }, []);

  useEffect(() => {
    if (props) setupdateRestaurantData(props);
  }, [props]);

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (!updateRestaurantData) return;
    setLoading(true);
    setMessage(null);

    const payload = {
      restourantName:            updateRestaurantData.restourantName,
      restourantMinAmount:       updateRestaurantData.restourantMinAmount,
      restourantEstDeliveryTime: 0,
      restourantIsEnabled:       updateRestaurantData.restourantIsEnabled,
      restourantPhoneNo:         updateRestaurantData.restourantPhoneNo,
      restourantAddress:         updateRestaurantData.restourantAddress,
      restourantManagerName:     updateRestaurantData.restourantManagerName,
      cityId:                    updateRestaurantData.cityId ?? updateRestaurantData.city?.id ?? "",
      restourantLatitude:        updateRestaurantData.restourantLatitude ?? 0,
      restourantLongitude:       updateRestaurantData.restourantLongitude ?? 0,
    };

    const call = formAction === "update" && props?.id
      ? putRestaurants(payload, props.id)
      : postRestaurant(updateRestaurantData);

    call
      .then(() => { setMessage({ type: "success", text: formAction === "update" ? "Restoran başarıyla güncellendi." : "Restoran başarıyla eklendi." }); onSaved?.(); })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description;
        setMessage({ type: "error", text: desc ?? "Bir hata oluştu. Lütfen tekrar deneyin." });
      })
      .finally(() => setLoading(false));
  };

  const set = (field: Partial<IRestaurantDetail>) =>
    setupdateRestaurantData((prev) => ({ ...prev!, ...field }));

  return (
    <div className="p-2">
      {/* Başlık */}
      <div className="d-flex align-items-center mb-4">
        <div style={{ width: "4px", height: "26px", background: "#3b82f6", borderRadius: "3px", marginRight: "10px" }} />
        <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
          <i className={`bi ${isUpdate ? "bi-pencil-square" : "bi-plus-circle"} me-2 text-primary`}></i>
          {isUpdate ? "Restoran Bilgilerini Düzenle" : "Yeni Restoran Ekle"}
        </h5>
      </div>

      {/* Bildirim */}
      {message && (
        <div
          className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-4"
          style={{
            background: message.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1.5px solid ${message.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: message.type === "success" ? "#166534" : "#991b1b",
            fontSize: "0.88rem",
          }}
        >
          <i className={`bi ${message.type === "success" ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row g-3">

          {/* Restoran Adı */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-shop me-1"></i>Restoran Adı
            </label>
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              placeholder="Restoran adını girin"
              value={updateRestaurantData?.restourantName ?? ""}
              onChange={(e) => set({ restourantName: e.target.value })}
              maxLength={30}
              required
            />
          </div>

          {/* Min Sipariş Tutarı */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-cash-coin me-1"></i>Min. Sipariş Tutarı (₺)
            </label>
            <input
              type="number"
              className="form-control"
              style={inputStyle}
              placeholder="0"
              value={updateRestaurantData?.restourantMinAmount ?? ""}
              onChange={(e) => set({ restourantMinAmount: parseFloat(e.target.value) })}
              min={0}
              step="0.01"
              required
            />
          </div>

          {/* Adres */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-geo-alt me-1"></i>Adres
            </label>
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              placeholder="Restoran adresi"
              value={updateRestaurantData?.restourantAddress ?? ""}
              onChange={(e) => set({ restourantAddress: e.target.value })}
              required
            />
          </div>

          {/* Tel No */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-telephone me-1"></i>Telefon Numarası
            </label>
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              placeholder="05XX XXX XX XX"
              value={updateRestaurantData?.restourantPhoneNo ?? ""}
              onChange={(e) => set({ restourantPhoneNo: e.target.value })}
              required
            />
          </div>

          {/* İrtibat Kişisi */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-person me-1"></i>İrtibat Kişisi
            </label>
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              placeholder="Yetkili kişi adı"
              value={updateRestaurantData?.restourantManagerName ?? ""}
              onChange={(e) => set({ restourantManagerName: e.target.value })}
              required
            />
          </div>

          {/* Enlem */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-geo me-1"></i>Enlem (Latitude)
            </label>
            <input
              type="number"
              className="form-control"
              style={inputStyle}
              placeholder="Örn. 41.015137"
              value={updateRestaurantData?.restourantLatitude ?? ""}
              onChange={(e) => set({ restourantLatitude: parseFloat(e.target.value) })}
              step="any"
              required
            />
          </div>

          {/* Boylam */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-geo me-1"></i>Boylam (Longitude)
            </label>
            <input
              type="number"
              className="form-control"
              style={inputStyle}
              placeholder="Örn. 28.979530"
              value={updateRestaurantData?.restourantLongitude ?? ""}
              onChange={(e) => set({ restourantLongitude: parseFloat(e.target.value) })}
              step="any"
              required
            />
          </div>

          {/* Şehir */}
          <div className="col-md-6">
            <label className="form-label small fw-semibold text-muted mb-1">
              <i className="bi bi-map me-1"></i>Şehir
            </label>
            <select
              className="form-select"
              style={inputStyle}
              value={updateRestaurantData?.cityId ?? updateRestaurantData?.city?.id ?? ""}
              onChange={(e) => set({ cityId: e.target.value })}
              required
            >
              <option value="" disabled>Şehir seçiniz...</option>
              {cityList?.map((city) => (
                <option key={city.id} value={city.id}>{city.cityNameTr}</option>
              ))}
            </select>
          </div>

          {/* Aktif Toggle */}
          <div className="col-12">
            <div
              className="d-flex align-items-center justify-content-between p-3 rounded-3"
              style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb", maxWidth: "480px" }}
            >
              <div>
                <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>Restoran Durumu</div>
                <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                  {updateRestaurantData?.restourantIsEnabled
                    ? "Restoran şu an açık ve sipariş alıyor"
                    : "Restoran şu an kapalı"}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span
                  className="badge"
                  style={{
                    background: updateRestaurantData?.restourantIsEnabled ? "#dcfce7" : "#fee2e2",
                    color: updateRestaurantData?.restourantIsEnabled ? "#166534" : "#991b1b",
                    borderRadius: "20px", fontSize: "0.72rem", padding: "4px 10px",
                  }}
                >
                  <i className={`bi ${updateRestaurantData?.restourantIsEnabled ? "bi-door-open-fill" : "bi-door-closed-fill"} me-1`}></i>
                  {updateRestaurantData?.restourantIsEnabled ? "Açık" : "Kapalı"}
                </span>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={updateRestaurantData?.restourantIsEnabled ?? false}
                    onChange={(e) => set({ restourantIsEnabled: e.target.checked })}
                    style={{ width: "2.5rem", height: "1.3rem", cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="col-12 mt-1">
            <button
              type="submit"
              disabled={loading}
              onClick={() => setFormAction(isUpdate ? "update" : "add")}
              className="btn"
              style={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                color: "#fff",
                fontWeight: 600,
                border: "none",
                boxShadow: "0 3px 10px rgba(59,130,246,0.3)",
                padding: "10px 32px",
                fontSize: "0.92rem",
              }}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />{isUpdate ? "Güncelleniyor..." : "Ekleniyor..."}</>
              ) : (
                <><i className={`bi ${isUpdate ? "bi-check-lg" : "bi-plus-lg"} me-1`}></i>{isUpdate ? "Değişiklikleri Kaydet" : "Restoran Ekle"}</>
              )}
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}

export default AddRestaurantPage;